/**
 * Hero date formatting — turns the hero's structured start/end date+time fields
 * into a single user-facing string, optionally appending the page's TZ abbrev.
 */

import moment from "moment";
import { formatDateLocal, parseLocalDate } from "@/lib/utils/date-utils";
import { getShortTimezoneName } from "@/lib/utils/timezones";

interface FormatHeroDateInput {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  /** IANA timezone — used only to derive the short abbreviation (e.g. "PT") shown after times. */
  timeZone?: string | null;
}

/**
 * Convert "HH:mm" (24hr) → "h:mm A" (12hr). Returns "" for invalid input.
 *
 * Uses moment's strict parser rather than the shared `formatTime` helper because hero-date
 * has a regression test that expects garbage time strings to be silently dropped (the shared
 * helper would emit "NaN:undefined AM").
 */
function formatTime(hhmm: string): string {
  const parsed = moment(hhmm, "HH:mm", true);
  return parsed.isValid() ? parsed.format("h:mm A") : "";
}

/**
 * Format the hero's start/end date+time pair into one display string.
 *
 * Returns the empty string when neither date is set (the renderer will fall
 * back to the legacy free-text `date` field or show a placeholder).
 *
 * Examples (TZ = America/Los_Angeles, abbrev = "PT"):
 *   { startDate: "2026-02-05" }
 *     → "Feb 5, 2026"
 *   { startDate: "2026-02-05", startTime: "09:00" }
 *     → "Feb 5, 2026 · 9:00 AM PT"
 *   { startDate: "2026-02-05", startTime: "09:00", endTime: "17:00" }
 *     → "Feb 5, 2026 · 9:00 AM – 5:00 PM PT"
 *   { startDate: "2026-02-05", endDate: "2026-02-08" }
 *     → "Feb 5 – Feb 8, 2026"
 *   { startDate: "2026-02-05", startTime: "09:00", endDate: "2026-02-08", endTime: "17:00" }
 *     → "Feb 5, 2026, 9:00 AM – Feb 8, 2026, 5:00 PM PT"
 *
 * @param input - structured hero date fields and optional timezone.
 * @returns Display string, or "" when no date is set.
 */
export function formatHeroDate(input: FormatHeroDateInput): string {
  const { startDate, startTime, endDate, endTime, timeZone } = input;
  if (!startDate && !endDate) return "";

  // Anchor the TZ abbrev to the start date so events in DST shoulder seasons get the right
  // variant (e.g. "PST" vs "PDT"). The early `if (!startDate && !endDate)` above guarantees
  // at least one of these is set, so `(startDate ?? endDate)!` is safe.
  const refDate = parseLocalDate((startDate ?? endDate)!);
  const tzAbbrev = timeZone ? getShortTimezoneName(timeZone, refDate) : null;

  const fmtFull = (d: string) => formatDateLocal(d, "MMM D, YYYY");
  const fmtShort = (d: string) => formatDateLocal(d, "MMM D");

  // Single date (no end date, or end date equals start date).
  if (!endDate || endDate === startDate) {
    if (!startDate) return "";
    let result = fmtFull(startDate);
    const validStartTime = startTime ? formatTime(startTime) : "";
    const validEndTime = endTime ? formatTime(endTime) : "";

    if (validStartTime && validEndTime) {
      result += ` · ${validStartTime} – ${validEndTime}`;
    } else if (validStartTime) {
      result += ` · ${validStartTime}`;
    }
    if ((validStartTime || validEndTime) && tzAbbrev) {
      result += ` ${tzAbbrev}`;
    }
    return result;
  }

  // Date range.
  const validStartTime = startTime ? formatTime(startTime) : "";
  const validEndTime = endTime ? formatTime(endTime) : "";
  const hasAnyTime = Boolean(validStartTime || validEndTime);

  if (!startDate) {
    // Edge: only endDate set. Treat like single date.
    let result = fmtFull(endDate);
    if (validEndTime) result += ` · ${validEndTime}`;
    if (validEndTime && tzAbbrev) result += ` ${tzAbbrev}`;
    return result;
  }

  if (hasAnyTime) {
    // Times present — show full year on both sides + times so the range is unambiguous.
    const startStr = validStartTime ? `${fmtFull(startDate)}, ${validStartTime}` : fmtFull(startDate);
    const endStr = validEndTime ? `${fmtFull(endDate)}, ${validEndTime}` : fmtFull(endDate);
    return tzAbbrev ? `${startStr} – ${endStr} ${tzAbbrev}` : `${startStr} – ${endStr}`;
  }

  // No times: shorten when years match, and shorten further (drop the repeated month) when months match too.
  const sameYear = startDate.slice(0, 4) === endDate.slice(0, 4);
  const sameMonth = sameYear && startDate.slice(5, 7) === endDate.slice(5, 7);
  if (sameMonth) {
    return `${fmtShort(startDate)} – ${formatDateLocal(endDate, "D")}, ${endDate.slice(0, 4)}`;
  }
  return sameYear ? `${fmtShort(startDate)} – ${fmtFull(endDate)}` : `${fmtFull(startDate)} – ${fmtFull(endDate)}`;
}
